import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CreatePaymentParameters, CreatePaymentParametersBase, PaymentService } from '../services/payment.service';
import type { StripeElements } from '@stripe/stripe-js';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ApplicationService, ApplicationTier, Case } from '../gql/types';
import { ToastrService } from 'ngx-toastr';
import { FormControlWithElementDirective } from '../../widgets/form-control-with-element.directive';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgIf, NgFor, CurrencyPipe, TitleCasePipe } from '@angular/common';
import { ModalComponent } from '../components/modal/modal.component';

interface BasePayForServiceParams extends CreatePaymentParametersBase {
  service: ApplicationService;
  tier?: never;
  tiers?: never;
}

interface WithCase {
  appCase: Case;
  cases?: Case[];
}

interface WithCases {
  cases: Case[];
  appCase?: Case;
}

type PayForServiceParams = (WithCase | WithCases) & BasePayForServiceParams;

interface WithTiers {
  tiers: ApplicationTier[];
  tier?: ApplicationTier;
}

interface WithTier {
  tier: ApplicationTier;
  tiers?: ApplicationTier[];
}

interface BasePayForTierParams extends CreatePaymentParametersBase {
  appCase: Case;
  cases?: never;
  service?: never;
}

type PayForTierParams = (WithTier | WithTiers) & BasePayForTierParams;

export type PaymentModalParams = (PayForServiceParams | PayForTierParams) & {
  errorMessage?: string;
};

@Component({
  selector: 'payment-modal',
  templateUrl: './payment-modal.component.html',
  styleUrls: ['./payment-modal.component.scss'],
  standalone: true,
  imports: [
    ModalComponent,
    NgIf,
    FormsModule,
    NgFor,
    FaIconComponent,
    ReactiveFormsModule,
    FormControlWithElementDirective,
    CurrencyPipe,
    TitleCasePipe
  ]
})
export class PaymentModalComponent implements OnInit {
  private paymentIntentId: string;
  private stripeElements: StripeElements;

  public checkPriceError: string = '';
  public promoCode: string = '';
  public promoCodeDescription: string = '';
  public promoCodeError: string = '';
  public promoCodeFetched: boolean = false;
  public promoCodeValid: boolean = false;
  public price: number;
  public caseSelected: Case;
  public tierSelected: ApplicationTier;
  public terms = new UntypedFormControl();
  public paymentError: string;
  public paymentProcessing: boolean = false;
  public paymentSuccessful: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PaymentModalParams,
    private dialogRef: MatDialogRef<PaymentModalComponent>,
    private paymentService: PaymentService,
    private toastService: ToastrService
  ) {}

  async ngOnInit() {
    if (this.data.paymentIntentId) {
      this.paymentIntentId = this.data.paymentIntentId;
    }

    if (this.data.errorMessage) {
      this.showError(this.data.errorMessage);
    }

    if (this.data.appCase) {
      this.caseSelected = this.data.appCase;
    }
    if (this.data.service) {
      if (!this.data.appCase && !this.data.cases) {
        throw new Error('No case provided');
      }
    } else if ((this.data.tiers || this.data.tier) && this.data.appCase) {
      this.tierSelected = this.data.tiers ? this.data.tiers[0] : this.data.tier;
    } else {
      throw new Error('Not enough info provided');
    }

    let promoCode = this.data.promoCode;
    if (!promoCode) {
      const paymentMetadata = this.paymentService.getPaymentMetadata();
      if (paymentMetadata.referral_code) {
        promoCode = paymentMetadata.referral_code;
      }
    }
    if (promoCode) {
      this.promoCode = promoCode;
      await this.checkPromo(true);
    } else {
      await this.checkPriceAndLoadElements(true);
    }
  }

  checkPromo(forceLoadElements = false) {
    this.promoCodeDescription = '';
    this.promoCodeError = '';
    this.promoCodeFetched = false;
    this.promoCodeValid = false;

    if (!this.promoCode) {
      return this.checkPriceAndLoadElements(forceLoadElements);
    }

    this.paymentService.checkPromo(this.data.tier?.id, this.promoCode, this.data.service?.id).then((promoCode) => {
      this.promoCodeFetched = true;
      if (promoCode.valid) {
        this.promoCodeValid = true;
        this.promoCodeDescription = promoCode.promotion;
      } else {
        this.promoCodeError = promoCode.message;
      }
      return this.checkPriceAndLoadElements(forceLoadElements);
    });
  }

  private getPaymentPayload() {
    let payload: CreatePaymentParameters;

    if (this.data.service) {
      payload = {
        applicationCaseId: this.caseSelected?.id,
        serviceId: this.data.service.id
      };
    } else if (this.tierSelected) {
      payload = {
        applicationCaseId: this.caseSelected?.id,
        applicationTierId: this.tierSelected.id
      };
    }

    payload.paymentIntentId = this.paymentIntentId;
    payload.metadata = this.data.metadata || {};
    payload.metadata.accepted_terms = this.terms.value;
    if (this.promoCodeValid && this.promoCode) {
      payload.promoCode = this.promoCode;
    }

    return payload;
  }

  async checkPriceAndLoadElements(forceLoadElements = false) {
    this.checkPriceError = '';
    const oldPrice = this.price;

    let price;
    try {
      price = await this.paymentService.checkPrice(
        this.tierSelected?.id,
        this.promoCodeValid && this.promoCode,
        this.data.service?.id,
        this.caseSelected?.id
      );
      this.price = price;
    } catch (error) {
      console.error('Checking price error:', error);
      if (error.status) this.checkPriceError = error;
      return;
    }
    if (price === 0) {
      // No need to set up elements
      return;
    }

    return await this.paymentService
      .createPayment(this.getPaymentPayload())
      .then((result) => {
        if (oldPrice !== result.price || this.paymentIntentId != result.intent_id || forceLoadElements) {
          this.price = result.price;
          this.paymentIntentId = result.intent_id;
          this.stripeElements = this.paymentService.stripe.elements({
            clientSecret: result.client_secret
          });
          const paymentOptions = {
            // layout: {
            //  type: 'tabs',
            // 	defaultCollapsed: false,
            // },
            defaultValues: this.paymentService.getStripePaymentElementDefaultValues()
          };
          const paymentElement = this.stripeElements.create('payment', paymentOptions);
          paymentElement.mount('#payment-element');
        }
      })
      .catch((error) => {
        console.error('Checking price error:', error);
        this.checkPriceError = error;
      });
  }

  async submitPayment(event) {
    event.preventDefault();
    this.paymentProcessing = true;

    if (this.price === 0) {
      return this.paymentService
        .createPayment(this.getPaymentPayload())
        .then((result) => {
          this.paymentService.handleReceiptStatus(result.receipt_status, this);
          this.paymentProcessing = false;
        })
        .catch((error) => {
          console.error(error);
          this.paymentProcessing = false;
          this.paymentError = error.message || 'Unknown error, please contact support.';
        });
    }

    const base = { appCase: this.caseSelected, promoCode: this.promoCode };
    const return_url = this.paymentService.getReturnUrl(
      this.data.service ? { ...base, service: this.data.service } : { ...base, tier: this.tierSelected }
    );

    const { error, paymentIntent } = await this.paymentService.stripe.confirmPayment({
      elements: this.stripeElements,
      confirmParams: { return_url },
      redirect: 'if_required'
    });

    try {
      if (error) {
        console.error(error);
        this.paymentProcessing = false;
        if (
          error.code === 'payment_intent_unexpected_state' &&
          error.payment_intent &&
          error.payment_intent.status === 'succeeded'
        ) {
          await this.paymentService.handlePaymentIntentResult(
            error.payment_intent.id,
            error.payment_intent.status,
            this
          );
          this.paymentSuccessful = true;
          this.paymentProcessing = false;
        } else {
          this.toastService.error('Payment failed.');
          this.paymentProcessing = false;
        }
      } else if (paymentIntent) {
        await this.paymentService.handlePaymentIntentResult(paymentIntent.id, paymentIntent.status, this);
        this.paymentProcessing = false;
        this.paymentSuccessful = true;
      }
    } catch (error) {
      console.error(error);
      this.paymentError = error.message || 'Unknown error, please contact support.';
      this.paymentProcessing = false;
    }
  }

  get paymentButtonDisabled() {
    // be careful to keep this function super lightweight, it's used in a template expression
    return (
      !this.caseSelected ||
      !this.terms.value ||
      this.paymentProcessing ||
      this.paymentSuccessful ||
      this.checkPriceError ||
      (this.promoCode && !this.promoCodeValid)
    );
  }

  showError(error: string) {
    this.paymentError = error;
  }

  close() {
    this.dialogRef.close(true);
  }
}
